// Tech-hub landing page data — corporate corridors targeting Gemini's
// high-value DFW / LA / SF sub-hubs. Each hub is IT / large-employer focused
// with a micro-market angle for premium lead value.

export interface Hub {
  slug: string;
  name: string; // Display name / H1
  region: string; // "DFW Metroplex" / "Greater Los Angeles" / "SF Bay Area"
  state: string; // slug
  stateName: string; // display
  city: string; // primary city / label
  intro: string; // one-paragraph hero blurb
  targetTenants: string[]; // types of firms in this hub
  neighborhoods: string[]; // sub-areas covered
  microMarketAngle: string; // why micro-market fits this hub
  cityLinkText: string; // link back to broader city page
  cityLinkPath: string; // route for the linked city
  faqs: { q: string; a: string }[];
}

export const HUBS: Hub[] = [
  {
    slug: "plano-legacy-business-park",
    name: "Legacy Business Park (Plano)",
    region: "Dallas–Fort Worth Metroplex",
    state: "texas",
    stateName: "Texas",
    city: "Plano, TX",
    intro:
      "Free vending machines and micro-markets for corporate campuses inside Plano's Legacy Business Park. Toyota North America, JPMorgan Chase, Liberty Mutual, FedEx Office, Frito-Lay and dozens of regional HQs sit inside a 2,600-acre master-planned corridor — an ideal footprint for 24/7 breakroom service at zero cost.",
    targetTenants: [
      "Regional & national HQs",
      "Financial services",
      "Enterprise IT & SaaS",
      "Insurance",
      "Consumer packaged goods",
      "Shared-tenant Class A office towers",
    ],
    neighborhoods: [
      "Legacy West",
      "Legacy East",
      "Shops at Legacy",
      "Granite Park",
      "The Star (Frisco line)",
    ],
    microMarketAngle:
      "Legacy tenants routinely run 300+ on-site staff per floor across shared towers. That volume unlocks micro-market and smart-fridge placements with fresh meals, cold-brew, and premium snacks — well above what a traditional snack machine can support.",
    cityLinkText: "Plano, Texas coverage",
    cityLinkPath: "/states/texas/plano",
    faqs: [
      {
        q: "Do you place free vending machines inside Legacy West towers?",
        a: "Yes. We work with multi-tenant Class A buildings across Legacy West and Legacy East. Your total building headcount usually qualifies even if a single tenant only has 30–40 staff — the operator sizes the placement to the shared breakroom or lobby population.",
      },
      {
        q: "Can a 25-person tech tenant in Legacy still qualify?",
        a: "Absolutely. When you apply, tell us the total building headcount and shared breakroom access — smaller tenants inside a 200+ person floor typically get approved for a shared micro-market or smart-fridge unit funded by the building's aggregated traffic.",
      },
      {
        q: "What's the difference between a Legacy micro-market and a traditional vending machine?",
        a: "Micro-markets are self-checkout convenience stores with fresh food, premium coffee, energy drinks, and healthier options — right-sized for tech and financial-services campuses that expect Class-A amenities. Traditional snack/drink machines are still available for smaller placements.",
      },
    ],
  },
  {
    slug: "frisco-platinum-corridor",
    name: "Frisco North Platinum Corridor",
    region: "Dallas–Fort Worth Metroplex",
    state: "texas",
    stateName: "Texas",
    city: "Frisco, TX",
    intro:
      "Free vending machines and micro-markets for Frisco's booming North Platinum Corridor — including The Star (Dallas Cowboys HQ), Frisco Station, Hall Park, and Wade Park. New office towers, tech HQs, and sports-adjacent corporate spaces are prime placements for premium breakroom service at $0 cost.",
    targetTenants: [
      "Corporate HQ relocations from CA & NY",
      "Sports & entertainment offices",
      "Fintech & healthtech",
      "Professional services",
      "Multi-tenant office towers",
    ],
    neighborhoods: ["The Star", "Frisco Station", "Hall Park", "Wade Park", "Warren Sports Complex"],
    microMarketAngle:
      "Frisco tenants are almost universally new-build, class-A spaces expecting hotel-grade amenities. Micro-markets and smart fridges fit these campuses far better than legacy snack machines, and average revenue per placement here consistently tops $2,000/month.",
    cityLinkText: "Frisco, Texas coverage",
    cityLinkPath: "/states/texas/frisco",
    faqs: [
      {
        q: "Do you serve The Star and Frisco Station campuses?",
        a: "Yes. The Star's mixed-use campus and Frisco Station's office towers are within our active DFW route. Placement is typically approved within 48 hours of your site survey.",
      },
      {
        q: "What machine types are best for corporate Frisco offices?",
        a: "For 40+ headcount floors, a combo micro-market plus a beverage cooler out-performs snack-only vending 3-to-1. We'll recommend the mix during your free site survey.",
      },
      {
        q: "Is there a minimum contract?",
        a: "No contracts, no fees, no cost. Machines and micro-markets are placed and serviced free — we earn revenue purely from product sales.",
      },
    ],
  },
  {
    slug: "las-colinas-irving",
    name: "Las Colinas Corporate Center (Irving)",
    region: "Dallas–Fort Worth Metroplex",
    state: "texas",
    stateName: "Texas",
    city: "Irving, TX",
    intro:
      "Free vending machines and micro-markets across Las Colinas — 12,000 acres of master-planned corporate campus in Irving, home to ExxonMobil, McKesson, Kimberly-Clark, Nokia, Fluor, Vizient and 50+ Fortune 500 offices. Ideal footprint for high-volume, low-friction breakroom service at zero cost to the business.",
    targetTenants: [
      "Fortune 500 & Fortune 1000 HQs",
      "Energy & utilities",
      "Healthcare distribution",
      "Telecom & engineering",
      "Large multi-floor corporate towers",
    ],
    neighborhoods: ["Urban Center", "Williams Square", "Hidden Ridge", "Lake Carolyn", "Freeport"],
    microMarketAngle:
      "Las Colinas towers regularly house 500–2,000+ daily staff. That scale supports full smart-fridge markets plus multiple beverage placements per floor — grossing $3,000–$6,000+/month per site.",
    cityLinkText: "Irving, Texas coverage",
    cityLinkPath: "/states/texas/irving",
    faqs: [
      {
        q: "Can facilities managers request multi-floor placements?",
        a: "Yes. Large Las Colinas towers routinely get a micro-market on the main floor plus beverage machines on individual floors — all at $0 cost to the building.",
      },
      {
        q: "Do you handle security-badged corporate campuses?",
        a: "Yes. Our route drivers are background-checked and can be badged into corporate campuses. We'll match you with an operator experienced with your building's access protocol.",
      },
      {
        q: "What if we already have a vending vendor?",
        a: "We can quote as a replacement or an add-on (e.g. adding fresh food / micro-markets alongside existing snack machines). Zero cost either way.",
      },
    ],
  },
  {
    slug: "silicon-beach",
    name: "Silicon Beach (Santa Monica · Venice · Playa Vista)",
    region: "Greater Los Angeles",
    state: "california",
    stateName: "California",
    city: "Los Angeles, CA",
    intro:
      "Free micro-markets and smart-fridge placements across LA's Silicon Beach tech corridor — Snap, Google, YouTube, Hulu, Riot Games and hundreds of VC-backed startups sit inside the 3-mile stretch from Santa Monica through Venice to Playa Vista. These campuses expect Class-A breakroom amenities — we deliver them at $0 cost.",
    targetTenants: [
      "Tech startups (Series A+)",
      "Media & entertainment offices",
      "Gaming studios",
      "Ad tech & mar-tech",
      "Coworking & shared campuses",
    ],
    neighborhoods: [
      "Santa Monica",
      "Venice",
      "Playa Vista",
      "Marina del Rey",
      "Culver City (tech corridor)",
    ],
    microMarketAngle:
      "Silicon Beach engineers work odd hours and expect fresh meals, cold-brew, energy drinks, and healthier options — not vending sodas. Micro-markets and smart fridges are the right fit and consistently gross $2,000–$4,000/month per placement.",
    cityLinkText: "Los Angeles coverage",
    cityLinkPath: "/states/california/los-angeles",
    faqs: [
      {
        q: "Do 25-person startups qualify?",
        a: "In shared coworking or multi-tenant tech campuses, yes — total floor / building headcount usually exceeds our operator minimums. For standalone 25-person offices, a smart fridge is often the best fit.",
      },
      {
        q: "Can we get fresh food, not just snacks?",
        a: "Yes. Silicon Beach micro-markets typically carry fresh salads, wraps, cold-pressed juice, oat-milk cold-brew, and grab-and-go breakfast — all restocked 2–3× per week.",
      },
      {
        q: "How fast can Playa Vista campuses get service?",
        a: "Most Silicon Beach sites are surveyed within 48 hours and placed within 1–2 weeks of approval.",
      },
    ],
  },
  {
    slug: "downtown-los-angeles",
    name: "Downtown Los Angeles (DTLA)",
    region: "Greater Los Angeles",
    state: "california",
    stateName: "California",
    city: "Los Angeles, CA",
    intro:
      "Free vending machines and micro-markets across Downtown LA's corporate towers — from the Financial District to Bunker Hill, South Park and the Arts District. Legal, financial, and creative-agency floors with 100+ employees are the sweet spot for premium breakroom placement at zero cost.",
    targetTenants: [
      "Law & professional services firms",
      "Financial services",
      "Creative & advertising agencies",
      "Media production offices",
      "Multi-tenant Class A towers",
    ],
    neighborhoods: [
      "Financial District",
      "Bunker Hill",
      "South Park",
      "Arts District",
      "Historic Core",
    ],
    microMarketAngle:
      "DTLA towers house 500–3,000+ staff. Building-managed micro-markets on the ground floor plus beverage placements on individual floors is the highest-revenue pattern — funded entirely by product sales, not the building.",
    cityLinkText: "Los Angeles coverage",
    cityLinkPath: "/states/california/los-angeles",
    faqs: [
      {
        q: "Do you work with building management, or just tenants?",
        a: "Both. Building managers can arrange lobby / shared-amenity micro-markets that serve all tenants, and individual tenants can arrange floor-level machines. Both are $0 cost.",
      },
      {
        q: "What about after-hours access for restocking?",
        a: "Our DTLA route drivers coordinate with building security and can restock after hours or on schedules that don't disrupt tenants.",
      },
      {
        q: "Is placement really $0?",
        a: "Yes. No install fee, no service fee, no product minimums, no long-term contract. Our revenue comes from product sales.",
      },
    ],
  },
  {
    slug: "silicon-valley",
    name: "Silicon Valley (San Jose · Sunnyvale · Santa Clara)",
    region: "San Francisco Bay Area",
    state: "california",
    stateName: "California",
    city: "San Jose, CA",
    intro:
      "Free micro-markets, smart fridges, and coffee service across Silicon Valley — San Jose, Sunnyvale, Santa Clara, Mountain View, Palo Alto and the peninsula tech corridor. Apple, Google, Meta, NVIDIA, Intel, Cisco, and thousands of Series A–D startups sit inside this footprint. We place, stock, and service everything at $0 cost.",
    targetTenants: [
      "Big-tech HQs & satellite offices",
      "Series A–D startups",
      "Hardware & semiconductor",
      "AI & cloud infrastructure",
      "Tech-adjacent professional services",
    ],
    neighborhoods: [
      "San Jose",
      "Sunnyvale",
      "Santa Clara",
      "Mountain View",
      "Palo Alto",
      "Cupertino",
    ],
    microMarketAngle:
      "Silicon Valley campuses expect free-food or heavily-subsidized breakroom amenities. Micro-markets and smart fridges fit that culture — fresh food, cold-brew, energy drinks, and healthier options that engineers actually eat during 24/7 build cycles.",
    cityLinkText: "San Jose coverage",
    cityLinkPath: "/states/california/san-jose",
    faqs: [
      {
        q: "Can I get a smart fridge for a 20-person startup?",
        a: "Yes — smart fridges are the fastest-growing category for 20–50 person startups. Lower footprint than a full micro-market, and the tap-to-pay experience matches what engineers expect.",
      },
      {
        q: "Do you serve satellite offices, not just HQs?",
        a: "Yes. Satellite offices with 30+ daily headcount routinely qualify — either standalone or as an add-on to a multi-tenant building placement.",
      },
      {
        q: "Are there any monthly fees?",
        a: "None. Machines, restocking, cleaning, insurance, and repairs are all $0 to your business.",
      },
    ],
  },
  {
    slug: "san-francisco-soma-financial-district",
    name: "San Francisco Financial District & SOMA",
    region: "San Francisco Bay Area",
    state: "california",
    stateName: "California",
    city: "San Francisco, CA",
    intro:
      "Free vending machines, micro-markets and smart fridges across San Francisco's Financial District and SOMA — the densest concentration of tech HQs, fintech offices, and multi-tenant class-A towers on the West Coast. Salesforce Tower, Transamerica Pyramid, and the SOMA startup corridor are prime placements for zero-cost breakroom service.",
    targetTenants: [
      "Fintech & enterprise SaaS HQs",
      "AI startups",
      "Financial services & VC",
      "Legal & consulting firms",
      "Multi-tenant tech towers",
    ],
    neighborhoods: [
      "Financial District",
      "SOMA",
      "Mission Bay",
      "Yerba Buena",
      "Rincon Hill",
      "East Cut",
    ],
    microMarketAngle:
      "SF tenants expect Blue Bottle-grade coffee, fresh food, and healthier snack options. Micro-markets and smart fridges hit that bar — traditional snack machines feel outdated. Average placement here grosses $2,500–$5,000/month.",
    cityLinkText: "San Francisco coverage",
    cityLinkPath: "/states/california/san-francisco",
    faqs: [
      {
        q: "Do you handle SOMA startups in shared coworking?",
        a: "Yes. Shared coworking floors with 40+ daily headcount routinely qualify for a shared micro-market or smart-fridge placement.",
      },
      {
        q: "What about tech towers with existing vending?",
        a: "We often place alongside legacy vending — usually as an upgrade to fresh food + specialty coffee. Zero cost, and the operator handles the transition.",
      },
      {
        q: "How is this free?",
        a: "Our revenue comes from product sales, so high-traffic SF placements pay for themselves. Your business pays $0 — no install, service, or product fees.",
      },
    ],
  },
];

export const getHub = (slug: string) => HUBS.find((h) => h.slug === slug);
